/**
 * المرحلة 1.3 (P29) — a reviewer reads one doctor's application.
 *
 * The documents with their numbers whole (the reviewer compares them with
 * the scans), each scan on demand, a document accepted or sent back with a
 * reason, and the decision: approve with the specialty and the fee (which
 * opens a waiting account, as «تفعيل» does), or reject with a reason the
 * doctor reads on their application page, their bell, and by message when
 * the platform has a provider. The Super Admin reviews the platform's
 * applications from «طلبات التسجيل»; an institution's manager its own
 * doctors' from «التخصصات والأطباء».
 */

import { useEffect, useState } from 'react';
import { Badge, Button, Field, Icon, Input, Modal, Select, Spinner, Textarea } from '../../components/common';
import { applicationApi, categoryApi } from '../../api/endpoints';
import { useNotifications } from '../../context/NotificationContext';
import { categoryLabel, mergeCategories } from '../../domain/catalog';
import { formatDate } from '../../domain/locale';
import { useI18n } from '../../i18n/I18nProvider';

export function ApplicationReview({ applicationId, onDecided }) {
  const { t, lang } = useI18n();
  const { success, error: toastError } = useNotifications();
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const [categories, setCategories] = useState([]);
  const [viewing, setViewing] = useState(null);
  const [sendingBack, setSendingBack] = useState(null);
  const [decision, setDecision] = useState(null); // 'approve' | 'reject'
  const [form, setForm] = useState({ categoryId: '', priceExam: '', priceFollowup: '', reason: '' });
  const [busy, setBusy] = useState(false);

  const load = () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    applicationApi
      .get(applicationId)
      .then((data) => {
        setState({ loading: false, error: null, data });
        const profile = data.doctor?.DoctorProfile || {};
        setForm((f) => ({
          ...f,
          categoryId: data.application.categoryId || profile.categoryId || '',
          priceExam: data.application.priceExam ?? profile.priceExam ?? '',
          priceFollowup: data.application.priceFollowup ?? profile.priceFollowup ?? '',
        }));
      })
      .catch((error) => setState({ loading: false, error, data: null }));
  };
  useEffect(load, [applicationId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    categoryApi
      .listFor({ all: true })
      .catch(() => categoryApi.list())
      .then((rows) => setCategories(mergeCategories((rows || []).map((r) => ({ ...r, visibleToPatient: true })))))
      .catch(() => setCategories(mergeCategories([])));
  }, []);

  if (state.loading && !state.data) return <Spinner variant="inline" label={t('apply.loading')} />;
  if (state.error) return <p className="reason-note">{state.error.message}</p>;

  const { application, documents, required, missing, doctor } = state.data;

  const view = async (doc) => {
    try {
      setViewing(await applicationApi.image(application.id, doc.id));
    } catch (err) {
      toastError(err.message);
    }
  };

  const reviewDoc = async (doc, status, reason) => {
    setBusy(true);
    try {
      await applicationApi.reviewDocument(application.id, doc.id, status, reason);
      setSendingBack(null);
      load();
    } catch (err) {
      toastError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const decide = async () => {
    setBusy(true);
    try {
      if (decision === 'approve') {
        await applicationApi.approve(application.id, {
          categoryId: form.categoryId,
          priceExam: form.priceExam === '' ? undefined : Number(form.priceExam),
          priceFollowup: form.priceFollowup === '' ? undefined : Number(form.priceFollowup),
        });
        success(t('review13.approved', { name: doctor?.name || '' }));
      } else {
        await applicationApi.reject(application.id, form.reason.trim());
        success(t('review13.rejected'));
      }
      setDecision(null);
      onDecided?.();
      load();
    } catch (err) {
      toastError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="application-review">
      <div className="flex flex-wrap items-center gap-2">
        <Badge className="border-cyan-500/30 bg-cyan-500/10 text-cyan-300">{t(`apply.status.${application.status}`)}</Badge>
        {application.category && <Badge className="border-slate-600 bg-slate-800 text-slate-300">{categoryLabel(application.category, lang)}</Badge>}
        {missing.length > 0 && (
          <Badge className="border-amber-500/30 bg-amber-500/10 text-amber-300">
            {t('review13.missing', { kinds: missing.map((k) => t(`apply.kind.${k}`)).join(t('proProfile.joiner')) })}
          </Badge>
        )}
      </div>
      {application.decisionReason && (
        <p className="reason-note">
          <Icon name="message-square-text" size={14} className="mt-0.5 shrink-0" />
          <span>{t('review13.lastReason', { reason: application.decisionReason })}</span>
        </p>
      )}

      <div className="space-y-2">
        {documents.length === 0 && <p className="text-sm text-slate-400">{t('review13.noDocuments')}</p>}
        {documents.map((doc) => (
          <div key={doc.id} className={`doc-row doc-row--${doc.status}`} data-testid="review-doc">
            <span className="doc-row__icon">
              <Icon name="file-badge" size={18} />
            </span>
            <div className="min-w-0">
              <p className="flex flex-wrap items-center gap-2 text-sm font-bold text-white">
                {t(`apply.kind.${doc.kind}`)}
                {required.includes(doc.kind) && <span className="text-[10px] text-slate-400">{t('review13.required')}</span>}
                <span className={`doc-status doc-status--${doc.status}`}>{t(`apply.docStatus.${doc.status}`)}</span>
              </p>
              <p className="mt-0.5 text-xs text-slate-300">
                <span dir="ltr" className="font-mono">{doc.number}</span>
                {doc.issuedOn ? ` · ${t('review13.issued', { date: formatDate(doc.issuedOn) })}` : ''}
                {doc.expiresOn ? ` · ${t('apply.expires', { date: formatDate(doc.expiresOn) })}` : ''}
              </p>
              {doc.reason && <p className="mt-1 text-xs text-rose-300">{doc.reason}</p>}
            </div>
            <div className="doc-row__actions">
              <Button variant="subtle" icon="eye" onClick={() => view(doc)} data-testid="review-view">
                {t('apply.view')}
              </Button>
              {doc.status !== 'accepted' && (
                <Button variant="ghost" icon="check" onClick={() => reviewDoc(doc, 'accepted')} disabled={busy}>
                  {t('review13.accept')}
                </Button>
              )}
              {doc.status !== 'rejected' && (
                <Button variant="ghost" icon="x" onClick={() => setSendingBack({ doc, reason: '' })} disabled={busy}>
                  {t('review13.sendBack')}
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>

      {application.status !== 'approved' && (
        <div className="flex flex-wrap justify-end gap-2 border-t border-white/10 pt-4">
          <Button variant="danger" icon="x" onClick={() => setDecision('reject')} data-testid="application-reject">
            {t('review13.reject')}
          </Button>
          <Button variant="success" icon="check" onClick={() => setDecision('approve')} data-testid="application-approve">
            {t('review13.approve')}
          </Button>
        </div>
      )}

      <Modal
        open={Boolean(decision)}
        onClose={() => setDecision(null)}
        title={decision === 'approve' ? t('review13.approveTitle', { name: doctor?.name || '' }) : t('review13.rejectTitle', { name: doctor?.name || '' })}
        icon={decision === 'approve' ? 'badge-check' : 'file-x-2'}
        size="sm"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setDecision(null)} disabled={busy}>
              {t('common.cancel')}
            </Button>
            <Button
              variant={decision === 'approve' ? 'success' : 'danger'}
              icon={decision === 'approve' ? 'check' : 'send'}
              onClick={decide}
              loading={busy}
              disabled={decision === 'approve' ? !form.categoryId : !form.reason.trim()}
              data-testid="decision-confirm"
            >
              {decision === 'approve' ? t('review13.approve') : t('review13.reject')}
            </Button>
          </div>
        }
      >
        {decision === 'approve' ? (
          <div className="space-y-3">
            <Field label={t('apply.specialty')} required>
              <Select value={form.categoryId} onChange={(e) => setForm((f) => ({ ...f, categoryId: e.target.value }))} data-testid="approve-category">
                <option value="">{t('apply.pickSpecialty')}</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {categoryLabel(c, lang)}
                  </option>
                ))}
              </Select>
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label={t('review13.priceExam')}>
                <Input type="number" min={0} value={form.priceExam} onChange={(e) => setForm((f) => ({ ...f, priceExam: e.target.value }))} data-testid="approve-price" />
              </Field>
              <Field label={t('review13.priceFollowup')}>
                <Input type="number" min={0} value={form.priceFollowup} onChange={(e) => setForm((f) => ({ ...f, priceFollowup: e.target.value }))} />
              </Field>
            </div>
            <p className="text-[11px] leading-relaxed text-slate-400">{t('review13.approveHint')}</p>
          </div>
        ) : (
          <div className="space-y-3">
            <Field label={t('review13.reason')} required hint={t('review13.reasonHint')}>
              <Textarea rows={4} maxLength={500} value={form.reason} onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))} data-testid="reject-reason" />
            </Field>
          </div>
        )}
      </Modal>

      <Modal open={Boolean(sendingBack)} onClose={() => setSendingBack(null)} title={t('review13.sendBackTitle')} icon="file-x-2" size="sm"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setSendingBack(null)}>{t('common.cancel')}</Button>
            <Button variant="danger" icon="send" disabled={!sendingBack?.reason.trim()} loading={busy} onClick={() => reviewDoc(sendingBack.doc, 'rejected', sendingBack.reason.trim())}>
              {t('review13.sendBack')}
            </Button>
          </div>
        }
      >
        {sendingBack && (
          <Field label={t('review13.reason')} required>
            <Textarea rows={3} maxLength={300} value={sendingBack.reason} onChange={(e) => setSendingBack((s) => ({ ...s, reason: e.target.value }))} />
          </Field>
        )}
      </Modal>

      <Modal open={Boolean(viewing)} onClose={() => setViewing(null)} title={viewing ? t(`apply.kind.${viewing.kind}`) : ''} icon="file-badge" size="lg">
        {viewing && (String(viewing.image).startsWith('data:application/pdf') ? (
          <iframe src={viewing.image} title={t(`apply.kind.${viewing.kind}`)} className="h-[65vh] w-full rounded-lg" />
        ) : (
          <img src={viewing.image} alt={t(`apply.kind.${viewing.kind}`)} className="doc-scan" data-testid="review-scan" />
        ))}
      </Modal>
    </div>
  );
}

export default ApplicationReview;
